import React, { useState } from "react";
import { Lock, Copy, Check, ExternalLink, Pencil, X } from "lucide-react";

interface CourseCertificateProps {
  courseTitle: string;
  studentName: string;
  skills: string[];
  completedAt?: string;
  isUnlocked: boolean;
  completionPercentage?: number;
}

export default function CourseCertificate({
  courseTitle,
  studentName,
  skills,
  isUnlocked,
  completionPercentage = 0,
}: CourseCertificateProps) {
  const [copied, setCopied] = useState(false);

  const linkedInPostLines = [
    `🎓 Excited to share that I've successfully completed "${courseTitle}" course.`,
    ``,
    `Through this course, I gained knowledge and hands-on understanding in:`,
    ``,
    ...(skills.length > 0
      ? skills.map((s) => `• ${s}`)
      : [`• Key concepts and practical skills`]),
    ``,
    `Grateful for the opportunity to learn and looking forward to applying these skills in future projects.`,
    ``,
    `#Learning #StudentGrowth #Upskilling #ProfessionalDevelopment`,
  ];
  const linkedInPostText = linkedInPostLines.join("\n");
  const [postText, setPostText] = useState(() => linkedInPostText);
  const [isEditing, setIsEditing] = useState(false);

  const handleCopyAndShare = async () => {
    try {
      await navigator.clipboard.writeText(postText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleOpenLinkedIn = async () => {
    try {
      await navigator.clipboard.writeText(postText);
    } catch {
      /* ignore */
    }
    const pageUrl = encodeURIComponent(window.location.href);
    window.open(
      `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(postText)}&url=${pageUrl}`,

      "_blank",
      "noopener,noreferrer,width=700,height=700",
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Section header */}
      <div className="px-6 py-4 border-b border-gray-100 bg-linear-to-r from-gray-50 to-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-[#0a66c2] flex items-center justify-center">
            <svg viewBox="0 0 24 24" width="11" height="11" fill="white">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </div>
          <h3 className="text-base font-bold text-gray-900">
            Share Your Achievement
          </h3>
        </div>
        {!isUnlocked && (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-32 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#0a66c2] rounded-full transition-all"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 font-medium">
              {completionPercentage}% complete
            </span>
          </div>
        )}
      </div>

      {!isUnlocked ? (
        /* Locked state */
        <div className="flex flex-col items-center justify-center py-10 px-6">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <Lock className="h-7 w-7 text-gray-400" />
          </div>
          <p className="text-sm font-semibold text-gray-700 text-center mb-1">
            Complete the course to unlock sharing
          </p>
          <p className="text-xs text-gray-400 text-center">
            {completionPercentage}% done — {100 - completionPercentage}%
            remaining
          </p>
        </div>
      ) : (
        /* LinkedIn post preview panel */
        <div className="bg-linear-to-b from-[#f3f6f9] to-white p-4">
          {/* Panel header */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-gray-700 uppercase tracking-widest">
              Your LinkedIn Post Preview
            </span>
            <span className="ml-auto text-[10px] text-gray-400 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
              Ready to share
            </span>
          </div>

          {/* LinkedIn post card mock */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            {/* Post header */}
            <div className="flex items-center gap-3 px-4 pt-4 pb-3">
              <div className="w-11 h-11 rounded-full bg-linear-to-br from-[#0a66c2] to-[#004182] flex items-center justify-center text-white font-bold text-base shrink-0 select-none">
                {studentName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 leading-tight">
                  {studentName}
                </p>
                <p className="text-[10px] text-gray-400 leading-tight mt-0.5">
                  Just now · 🌐
                </p>
              </div>
              <button
                onClick={() => setIsEditing((v) => !v)}
                title={isEditing ? "Close editor" : "Edit post"}
                className={`shrink-0 p-1.5 rounded-lg transition-colors ${
                  isEditing
                    ? "bg-[#0a66c2]/10 text-[#0a66c2] hover:bg-[#0a66c2]/20"
                    : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                }`}
              >
                {isEditing ? (
                  <X className="h-4 w-4" />
                ) : (
                  <Pencil className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* Post body */}
            <div className="px-4 pb-4">
              {isEditing ? (
                <>
                  <textarea
                    autoFocus
                    value={postText}
                    onChange={(e) => setPostText(e.target.value)}
                    rows={12}
                    className="w-full text-sm text-gray-800 leading-relaxed font-[system-ui] resize-none rounded-lg border border-[#0a66c2] bg-white ring-1 ring-[#0a66c2] outline-none px-3 py-2.5 transition-colors"
                    placeholder="Edit your post…"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <button
                      onClick={() => { setPostText(linkedInPostText); }}
                      className="text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      Reset to default
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="text-[10px] font-semibold text-[#0a66c2] hover:text-[#004182] transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap font-[system-ui] select-text">
                  {postText}
                </p>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleCopyAndShare}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-semibold transition-all ${
                copied
                  ? "bg-green-50 border-green-300 text-green-700"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
              }`}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" /> Copied to clipboard!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" /> Copy post text
                </>
              )}
            </button>
            <button
              onClick={handleOpenLinkedIn}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#0a66c2] hover:bg-[#004182] text-white text-sm font-semibold transition-colors shadow-sm"
            >
              <ExternalLink className="h-4 w-4" />
              Open LinkedIn &amp; Post
            </button>
          </div>
          <p className="text-center text-[10px] text-gray-400 mt-2">
            Text is auto-copied · paste it into LinkedIn's post editor
          </p>
        </div>
      )}
    </div>
  );
}
